import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "./ui/LoadingComponent";
import { apiCall } from "../Apis/apiCall";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import Shimmer from "./ui/Shimmer";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";
import CustomSelect from "./ui/CustomSelect";

const WorkflowsLayer = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: "created_at",
    direction: "asc",
  });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fetchWorkflows = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = "created_at",
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
        ...(search && { search: encodeURIComponent(search) }),
        ...(sortBy && { sort_by: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      });

      const response = await apiCall(
        "get",
        `/get-all-workflows-bo?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.errorCode === 0) {
        const { data = [], pagination = {} } = response.data;
        const {
          current_page = page,
          per_page = perPage,
          total = 0,
        } = pagination;
        const mappedWorkflows = data.map((workflow) => ({
          ...workflow,
          workflow_name: workflow.workflow_name,
        }));
        setWorkflows(mappedWorkflows);
        setTotalEntries(total);
        setCurrentPage(parseInt(current_page, 10));
        setTotalPages(Math.ceil(total / per_page));

        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        handleCustomResponse("error", "No workflows found.");
        setWorkflows([]);
        setTotalEntries(0);
        setTotalPages(1);
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      setWorkflows([]);
      setTotalEntries(0);
      setTotalPages(1);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      fetchWorkflows(1, perPage, query, sortConfig.field, sortConfig.direction);
    }, 500),
    [perPage, sortConfig.field, sortConfig.direction]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    fetchWorkflows(currentPage, perPage, searchQuery, field, newDirection);
  };

  const sortedWorkflows = useMemo(() => {
    return [...workflows].sort((a, b) => {
      if (!sortConfig.field) return 0;
      const valueA = a[sortConfig.field] ?? "";
      const valueB = b[sortConfig.field] ?? "";

      if (sortConfig.field === "is_active") {
        const valA = valueA ? "Active" : "Inactive";
        const valB = valueB ? "Active" : "Inactive";
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if (sortConfig.field === "is_default") {
        const valA = valueA ? "Yes" : "No";
        const valB = valueB ? "Yes" : "No";
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString())
        : valueB.toString().localeCompare(valueA.toString());
    });
  }, [workflows, sortConfig]);

  useEffect(() => {
    fetchWorkflows(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [currentPage, perPage, sortConfig.field, sortConfig.direction]);

  const handleOpenModal = (type, workflow = null) => {
    setModalType(type);
    setShowModal(true);
    setWorkflowId(null);
    setWorkflowName("");
    setIsActive(true);
    setSelectedWorkflow(null);

    if (workflow) {
      if (!workflow.workflow_id || isNaN(workflow.workflow_id)) {
        handleCustomResponse("error", "Invalid Workflow ID.");
        return;
      }
      setSelectedWorkflow(workflow);
      setWorkflowId(workflow.workflow_id);
      if (type === "update") {
        setModalLoading(true);
        setTimeout(() => {
          setWorkflowName(workflow.workflow_name || "");
          setIsActive(workflow.is_active || false);
          setModalLoading(false);
        }, 1000);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setWorkflowId(null);
    setWorkflowName("");
    setIsActive(true);
    setSelectedWorkflow(null);
    setModalLoading(false);
  };

  const handleAddWorkflow = async () => {
    if (!workflowName.trim()) {
      handleCustomResponse("error", "Workflow name is required.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "post",
        "/create-default-workflow",
        { workflow_name: workflowName },
        navigate
      );

      if (response?.data?.errorCode === 0) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        setWorkflowName("");
        await fetchWorkflows(currentPage, perPage, searchQuery);
        handleCloseModal();
        const workflowData = response?.data?.workflow;
        const workflow = {
          workflow_id: workflowData.workflow_id,
          workflow_name: workflowData.workflow_name,
        };
        navigate("/edit-work-flow", {
          state: { workflow },
        });
      } else {
        handleCustomResponse("error", "Failed to add workflow.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowId || isNaN(workflowId) || workflowId === 1) {
      handleCustomResponse("error", "Cannot delete: Invalid Workflow ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "post",
        `/delete-workflow/${workflowId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        await fetchWorkflows(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete workflow.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDuplicateWorkflow = async () => {
    if (!workflowId || isNaN(workflowId)) {
      handleCustomResponse("error", "Cannot duplicate: Invalid Workflow ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "post",
        `/duplicate-workflow/${workflowId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "POST");
        await fetchWorkflows(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to duplicate workflow.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateWorkflow = async () => {
    if (!workflowId || isNaN(workflowId)) {
      handleCustomResponse("error", "Cannot update: Invalid Workflow ID.");
      return;
    }
    if (!workflowName.trim()) {
      handleCustomResponse("error", "Workflow name is required.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "put",
        `/update-workflow-name/${workflowId}`,
        { workflow_name: workflowName, is_active: isActive },
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        await fetchWorkflows(currentPage, perPage, searchQuery);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to update workflow.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") handleAddWorkflow();
    else if (modalType === "delete") handleDeleteWorkflow();
    else if (modalType === "duplicate") handleDuplicateWorkflow();
    else if (modalType === "update") handleUpdateWorkflow();
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);
  if (error) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by workflow name..."
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Search workflows"
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
              }}
            />
            <div className="ms-auto">
              {hasPermission("insert_workflow") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => handleOpenModal("add")}
                  aria-label="Add new workflow"
                >
                  <Icon icon="ri:add-line" className="me-1" /> Add Workflow
                </button>
              )}
            </div>
          </div>

          {sortedWorkflows.length === 0 && !loading ? (
            <TableDataNoAvailable message="No workflows found." />
          ) : (
            <div className="position-relative">
              <div
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <div className="table-responsive">
                  <table
                    className="table bordered-table mb-0 table-hover"
                    aria-label="Workflows table"
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col">S.No</th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("workflow_name")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "workflow_name"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Workflow Name</span>
                            <SortIcons
                              field="workflow_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_default")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "is_default"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Default</span>
                            <SortIcons
                              field="is_default"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_at")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "created_at"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_active")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "is_active"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Status</span>
                            <SortIcons
                              field="is_active"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWorkflows.map((workflow, index) => (
                        <tr key={workflow.workflow_id || index}>
                          <td>{startEntry + index}</td>
                          <td className="p-2">
                            <span className="">
                              {workflow.workflow_name || "-"}
                            </span>
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                workflow.is_default
                                  ? "bg-info-focus text-info-600 border border-info-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"

                              }`}
                            >
                              {workflow.is_default ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="p-2">
                            {workflow.created_at || "-"}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                workflow.is_active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {workflow.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">
                            <div className="d-flex gap-2">
                              {hasPermission("insert_workflow") && (
                                <button
                                  className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    navigate("/edit-work-flow", {
                                      state: { workflow },
                                    })
                                  }
                                  aria-label={`Navigate to edit workflow page for ${
                                    workflow.workflow_name || "-"
                                  }`}
                                >
                                  <Icon icon="material-symbols:arrow-forward" />
                                  <span className="tooltip">
                                    Go to Workflow
                                  </span>
                                </button>
                              )}
                              {hasPermission("insert_workflow") && (
                                <button
                                  className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    handleOpenModal("update", workflow)
                                  }
                                  aria-label={`Edit workflow ${
                                    workflow.workflow_name || "-"
                                  }`}
                                >
                                  <Icon icon="lucide:edit" />
                                  <span className="tooltip">
                                    Edit Name/Status
                                  </span>
                                </button>
                              )}
                              {hasPermission("insert_workflow") && (
                                <button
                                  className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    handleOpenModal("duplicate", workflow)
                                  }
                                  aria-label={`Duplicate workflow ${
                                    workflow.workflow_name || "-"
                                  }`}
                                >
                                  <Icon icon="mdi:content-duplicate" />
                                  <span className="tooltip">Duplicate</span>
                                </button>
                              )}
                              {hasPermission("delete_workflow") && (
                                <button
                                  className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    handleOpenModal("delete", workflow)
                                  }
                                  aria-label={`Delete workflow ${
                                    workflow.workflow_name || "-"
                                  }`}
                                >
                                  <Icon icon="mingcute:delete-2-line" />
                                  <span className="tooltip">Delete</span>
                                </button>
                              )}
                              {hasPermission("assign_workflow") && (
                                <button
                                  className="w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    navigate("/assign-workflow", {
                                      state: {
                                        selectedWorkflow: {
                                          id: workflow.workflow_id,
                                          name: workflow.workflow_name,
                                        },
                                      },
                                    })
                                  }
                                  aria-label={`Assign workflow ${
                                    workflow.workflow_name || "-"
                                  }`}
                                >
                                  <Icon icon="mdi:account-group" />
                                  <span className="tooltip">
                                    Assign Workflow
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-hidden={!showModal}
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalTitle">
                {modalType === "add"
                  ? "Add New Workflow"
                  : modalType === "delete"
                  ? "Delete Workflow"
                  : modalType === "duplicate"
                  ? "Duplicate Workflow"
                  : "Update Workflow"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close modal"
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {selectedWorkflow?.workflow_name || "this workflow"}
                  </strong>
                  ?
                </p>
              ) : modalType === "duplicate" ? (
                <p>
                  Are you sure you want to duplicate{" "}
                  <strong>
                    {selectedWorkflow?.workflow_name || "this workflow"}
                  </strong>
                  ?
                </p>
              ) : modalType === "update" ? (
  <>
    <div className="mb-3">
      <label htmlFor="workflowName" className="form-label">
        Workflow Name
      </label>
      <input
        type="text"
        className="form-control"
        id="workflowName"
        value={workflowName}
        onChange={(e) => setWorkflowName(e.target.value)}
        placeholder="Enter workflow name"
        aria-describedby="workflowNameHelp"
        required
      />
    </div>
    {/* 🔥 STATUS FIELD - CUSTOM SELECT 🔥 */}
    <div className="mb-3">
      <label htmlFor="isActive" className="form-label">
        Status
      </label>
      <CustomSelect
        name="isActive"
        value={
          isActive === true
            ? { label: "Active", value: "true" }
            : isActive === false
            ? { label: "Inactive", value: "false" }
            : null
        }
        onChange={(selectedOption) => {
          const booleanValue = selectedOption?.value === "true";
          setIsActive(booleanValue);
        }}
        options={[
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ]}
        placeholder="Select status"
        labelKey="label"
        valueKey="value"
        isClearable={false}
        isSearchable={false}
      />
    </div>
    {/* 🔥 END 🔥 */}
  </>
) : (
                <div className="mb-3">
                  <label htmlFor="workflowName" className="form-label">
                    Workflow Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="workflowName"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name"
                    aria-describedby="workflowNameHelp"
                    required
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete"
                    ? "btn-danger"
                    : modalType === "duplicate"
                    ? "btn-primary-600"
                    : ""
                }`}
                onClick={handleSubmit}
                disabled={saveLoading || modalLoading}
                aria-label={
                  modalType === "delete"
                    ? "Confirm delete"
                    : modalType === "duplicate"
                    ? "Confirm duplicate"
                    : modalType === "update"
                    ? "Update workflow"
                    : "Add workflow"
                }
              >
                {modalType === "add"
                  ? saveLoading
                    ? "Adding..."
                    : "Save Workflow"
                  : modalType === "delete"
                  ? saveLoading
                    ? "Deleting..."
                    : "Delete Workflow"
                  : modalType === "duplicate"
                  ? saveLoading
                    ? "Duplicating..."
                    : "Duplicate Workflow"
                  : saveLoading
                  ? "Updating..."
                  : "Update Workflow"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .position-relative {
            position: relative;
          }
          .tooltip {
            visibility: hidden;
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background-color: #333;
            color: white;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 1000;
            opacity: 0;
            transition: opacity 0.2s;
          }
          button:hover .tooltip {
            visibility: visible;
            opacity: 1;
          }
        `}
      </style>
    </div>
  );
};

export default WorkflowsLayer;
